/**
 * Hook composing scroll-depth, exit-intent, and timer triggers.
 * Per-project callback blocking uses enquiryProjects.hasRequestedProjectCallback.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import useExitIntent from '../../hooks/useExitIntent';
import useScrollDepth from '../../hooks/useScrollDepth';
import { hasRequestedProjectCallback } from '../../lib/enquiryProjects';

function sessionFlag(key) {
    if (typeof window === 'undefined') return { get: () => false, set: () => {} };
    return {
        get: () => {
            try { return window.sessionStorage.getItem(key) === '1'; } catch (_e) { return false; }
        },
        set: () => {
            try { window.sessionStorage.setItem(key, '1'); } catch (_e) { /* ignore */ }
        },
    };
}

export default function useEnquiryTriggers(cfg) {
    const {
        mode = 'global',
        projectKey = '',
        scrollDepth = false,
        scrollPercent = 0.6,
        exitIntent = false,
        minTimeOnPageMs = 0,
        onShow,
        onAlreadyRequested,
    } = cfg || {};

    const onShowRef = useRef(onShow);
    onShowRef.current = onShow;
    const onAlreadyRef = useRef(onAlreadyRequested);
    onAlreadyRef.current = onAlreadyRequested;

    const flagKey = mode === 'project'
        ? 'enquiry_shown_project'
        : mode === 'blog'
            ? 'enquiry_shown_blog'
            : 'enquiry_shown_global';
    const flag = sessionFlag(flagKey);

    const projectBlocked = useCallback(
        () => Boolean(projectKey) && hasRequestedProjectCallback(projectKey),
        [projectKey],
    );

    const [enabled, setEnabled] = useState(() => !projectBlocked() && !flag.get());

    useEffect(() => {
        setEnabled(!projectBlocked() && !flag.get());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectKey]);

    const fire = useCallback((triggerType) => {
        if (!enabled) return;
        if (projectBlocked()) return;
        if (flag.get()) return;
        flag.set();
        setEnabled(false);
        try { onShowRef.current && onShowRef.current(triggerType); } catch (_e) { /* ignore */ }
    }, [enabled, flag, projectBlocked]);

    useScrollDepth(scrollPercent, () => fire('scroll_depth'), { enabled: enabled && scrollDepth });
    useExitIntent(() => fire('exit_intent'), { enabled: enabled && exitIntent, minDelayMs: minTimeOnPageMs });

    return {
        openManually: (triggerType = 'manual') => {
            if (projectBlocked()) {
                try { onAlreadyRef.current && onAlreadyRef.current(); } catch (_e) { /* ignore */ }
                return false;
            }
            try { onShowRef.current && onShowRef.current(triggerType); } catch (_e) { /* ignore */ }
            return true;
        },
        isEnabled: enabled,
        alreadyRequested: projectBlocked(),
    };
}
